import { delay } from 'redux-saga';
import { all, select, fork, take, put, race, call, takeEvery } from 'redux-saga/effects';
import { LOCATION_CHANGE } from 'react-router-redux';

import triggerResize from '@xanadu/ui-shared/utils/triggerResize';

import {
  TOGGLE_NAVIGATION,
  NAVIGATION_SHOW_PANEL
} from '@xanadu/ui-shared/modules/navigation/actions';

import {
  ADD_SELECTIONS_TO_MULTIPLE
} from '@xanadu/ui-shared/modules/multiples/actions';

import {
  STORE_UNSUBMITTED_OFFERS,
} from '@xanadu/ui-shared/modules/unsubmittedOffers/actions';

import { BET_SOURCES } from '@xanadu/ui-shared/constants/unsubmittedOffers';

import {
  CLOSE_OVERLAY
} from '../overlay/actions';

import {
  updateRightSidebar,
  triggerUpdateRightSidebar,
  TRIGGER_UPDATE_RIGHT_SIDEBAR,
  UPDATE_RIGHT_SIDEBAR
} from './actions';

import {
  LOGIN_SUCCESS,
  LOGOUT_SUCCESS
} from '@xanadu/ui-shared/modules/userSession/actions';

import { updateFlags } from '../../configureFlags';
import { IS_MOBILE } from '../../constants';

import * as api from './api';

function* initializeSideBar() {
  if (IS_MOBILE) {
    yield put(updateRightSidebar({ visible: false, content: '' }));
  }
}

function* watchLogoutLoginTriggers() {
  while (true) {
    const { login, logout } = yield race({
      login: take(LOGIN_SUCCESS),
      logout: take(LOGOUT_SUCCESS)
    });
    const sidebarContent = yield select(state => state.getIn(['rightSidebar', 'content']));
    const widthSize = yield select(state => state.getIn(['application', 'widthSize']));

    if (
      (logout && sidebarContent === 'account') ||
      (login && sidebarContent === 'settings')
    ) {
      if (widthSize <= 1200) {
        yield put(updateRightSidebar({
          content: null,
          visible: false
        }));
      } else {
        yield put(updateRightSidebar({
          content: 'betslip',
          visible: true
        }));
      }
    }
  }
}

function* watchSideBarUpdate() {
  const eligibleToStore = ['betslip', 'multiples'];

  while (true) {
    const action = yield take(TRIGGER_UPDATE_RIGHT_SIDEBAR);
    if (action.payload) {
      const { visible, content } = action.payload;

      yield put(updateRightSidebar(action.payload));

      if (eligibleToStore.includes(content) && window.innerWidth > 1200) {
        updateFlags('mb-right-sidebar-visible', visible);
        updateFlags('mb-right-sidebar-content', content);
      }

      yield call(delay, 200);

      yield call(triggerResize);
    }
  }
}

function* watchSideBarUpdateForScrollHandling() {
  while (true) {
    const action = yield take(UPDATE_RIGHT_SIDEBAR);
    if (action.payload) {
      const { visible } = action.payload;

      api.setBodyScrollOverflow(visible);
    }
  }
}

let prevLocation = null;
function* handleLocationChanges({ payload }) {
  const { pathname } = payload;

  if (prevLocation !== pathname) {
    const isCasinoPage = pathname.startsWith('/casino');
    const isResponsibleGamblingPage = pathname.startsWith('/safer-gambling');
    const isReports = pathname.startsWith('/reports');

    if (isCasinoPage || isResponsibleGamblingPage || isReports) {
      yield put(updateRightSidebar({
        visible: false,
        content: null
      }));
    }

    prevLocation = pathname;
  }
}

function* watchExternalTriggers() {
  while (true) {
    const {
      overlay,
      navigation,
      navShowPanel
    } = yield race({
      overlay: take(CLOSE_OVERLAY),
      navigation: take(TOGGLE_NAVIGATION),
      navShowPanel: take(NAVIGATION_SHOW_PANEL),
    });

    if (window.innerWidth < 1200) {
      if (
        (navigation && navigation.payload) ||
        (overlay) ||
        (navShowPanel && navShowPanel.payload)
      ) yield put(triggerUpdateRightSidebar({ visible: false }));
    }
  }
}

function* watchForUnsubmittedOffersAddition() {
  while (true) {
    const { payload: offers } = yield take(STORE_UNSUBMITTED_OFFERS);
    const rightSidebarContent = yield select((state) => state.getIn(['rightSidebar', 'content']));

    const hasBetBuilderBets = offers.some(({ source }) => source === BET_SOURCES.betBuilder);

    if (rightSidebarContent !== 'multiples' || hasBetBuilderBets) {
      yield put(
        triggerUpdateRightSidebar({
          content: 'betslip',
          visible: true,
        })
      );
    }
  }
}

function* watchForMultipleSelectionsAddition() {
  while (true) {
    yield take(ADD_SELECTIONS_TO_MULTIPLE);

    const [content, visible] = yield select(state => [
      state.getIn(['rightSidebar', 'content']),
      state.getIn(['rightSidebar', 'visible']),
    ]);

    if (content === 'multiples' && !visible) {
      yield put(triggerUpdateRightSidebar({
        visible: true,
        content: 'multiples'
      }));
    }
  }
}

export default function* root() {
  yield all([
    takeEvery(LOCATION_CHANGE, handleLocationChanges),
    fork(watchSideBarUpdate),
    fork(watchLogoutLoginTriggers),
    fork(watchSideBarUpdateForScrollHandling),
    fork(watchExternalTriggers),
    fork(initializeSideBar),
    fork(watchForUnsubmittedOffersAddition),
    fork(watchForMultipleSelectionsAddition),
  ]);
}
